
import Vue from 'vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { Data as AuthorData } from '@root/modules/author/types/author';
import getAliasByLocale from '@root/common/utils/getAliasByLocale';
import AuthorImage from '@root/modules/author/components/AuthorImage.vue';
import { isObject } from '@core/utils/helpers';
import { faEnvelope } from '@delfi.lt/fa-kit/icons/modules/classic/solid';
import { faFacebookF, faLinkedinIn, faXTwitter } from '@delfi.lt/fa-kit/icons/modules/classic/brands';

interface Props {
  author: AuthorData;
  size: string;
}

interface Computed {
  classModifier: string[];
  link: string | null;
  picture: string;
  position: string | undefined;
  description: string | undefined;
  socialMediaUrls: Record<string, any> | undefined;
}

export default Vue.extend<unknown, unknown, Computed, Props>({
  components: {
    LinkHandler,
    AuthorImage,
  },
  props: {
    author: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      faEnvelope,
      faFacebookF,
      faLinkedinIn,
      faXTwitter,
    };
  },
  computed: {
    classModifier() {
      const classes: string[] = [`author--${this.size}`];

      if (!this.picture) {
        classes.push(`author--no-picture`);
      }

      return classes;
    },
    link() {
      const authorSlug = this.author.slug;
      const hasAuthorPage = this.author.attributes?.find((item) => item.key === 'authorPage')?.value === true;
      const isAuthorPage = this.$route.params.slug === authorSlug;
      const path = getAliasByLocale(this.$channelConfig('settings').locale, 'author');

      return !hasAuthorPage || isAuthorPage ? null : `/${path}/${authorSlug}/`;
    },
    picture() {
      return this?.author?.image?.id || '';
    },
    position() {
      return this.author?.attributes?.find((item) => item.key === 'position')?.value;
    },
    description() {
      let description;

      if (
        isObject(this.author?.attributes?.find((item) => item.key === 'description')?.value) &&
        this.author?.attributes?.find((item) => item.key === 'description')?.value.html
      ) {
        description = this.author?.attributes?.find((item) => item.key === 'description')?.value?.html;
      } else {
        description = this.author?.attributes?.find((item) => item.key === 'description')?.value?.replaceAll('\n', '<br>');
      }

      return description;
    },
    socialMediaUrls() {
      const urls: Record<string, any> = {};

      const email = this.author?.attributes?.find((item) => item.key === 'email')?.value;
      if (email) {
        urls.email = `mailto:${email}`;
      }

      const value = this.author?.attributes?.find((item) => item.key === 'socialMediaUrls')?.value as unknown as string[];
      if (value && value.length) {
        value.forEach((url) => {
          if (/facebook/.test(url)) {
            urls.facebook = url;
          } else if (/linkedin/.test(url)) {
            urls.linkedin = url;
          } else if (/(x{1}|twitter)\.com/.test(url)) {
            urls.twitter = url;
          }
        });
      }

      return urls;
    },
  },
});
